// @eidollon/geo/types
//
// Zod-validated primitives for the hierarchical region model. The four
// semantic levels (country / admin1 / regional / city) are the canonical
// vocabulary across services/api, services/tile-edge, packages/intel-feed-client,
// and apps/terminal. OSM admin_level integers are mapped to these per-country
// via admin-level-map.ts — downstream code never sees raw OSM integers.
//
// Master plan: docs/designs/intel-map-terrain-and-region-hierarchy.md (D3, D4).
// Iron Rule #11: this is the platform primitive — do not fork these types.

import { z } from 'zod';

export const RegionLevel = z.enum(['country', 'admin1', 'regional', 'city']);
export type RegionLevel = z.infer<typeof RegionLevel>;

export const RegionId = z.string().uuid();
export type RegionId = z.infer<typeof RegionId>;

export const Iso2 = z
  .string()
  .length(2)
  .regex(/^[A-Z]{2}$/, 'ISO-3166-1 alpha-2, uppercase');
export type Iso2 = z.infer<typeof Iso2>;

/** OSM admin_level — 0..11 valid range; we map only a subset (2,4,6,8). */
export const OsmAdminLevel = z.number().int().min(0).max(11);
export type OsmAdminLevel = z.infer<typeof OsmAdminLevel>;

/** Bounding box in GeoJSON [west, south, east, north] order. */
export const Bbox = z.tuple([z.number(), z.number(), z.number(), z.number()]);
export type Bbox = z.infer<typeof Bbox>;

export const RegionNode = z.object({
  id: RegionId,
  name: z.string().min(1),
  level: RegionLevel,
  parentId: RegionId.nullable(),
  iso2: Iso2,
  slug: z.string().min(1),
  bbox: Bbox.nullable(),
});
export type RegionNode = z.infer<typeof RegionNode>;
